// import external libraries
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import components
import { Tooltip } from '@mui/material';
import { IconButton } from '@sdb/planning-ui';
import { FilterPopper } from 'components/flex-portal/FlexMatch/components/FilterPopper';
import { PulsingBadge } from 'components/shared/Badge/PulsingBadge';
import BacklogFilterFields from './components/BacklogFilterFields';

// import constants
import { BACKLOG_ENTITY_FILTER_TYPES } from '../../Backlog.consts';

// import utils
import { countActiveFilters } from './BacklogFilters.utils';

// import styles
import { StyledRelativeBox } from 'components/shared/Box/Box.styles';

// import hooks
import { useIsVerticalToolbar } from 'components/shared/ToolbarSet/ToolbarPosition.context';

// import types
import type {
  BacklogEmployeeViewFilters,
  BacklogEntityFilter,
  BacklogOrganizationalUnitId
} from '../../Backlog.types';
import type {
  BacklogEntityFilterType,
  BacklogFiltersForm,
  BacklogFiltersProps
} from './BacklogFilters.types';

/** The trigger's DOM id, which the popper reads back to anchor itself under the icon. */
const FILTER_ID = 'backlog-filters';

/**
 * The draft the popover opens with: whatever is currently applied. Rebuilt from the applied filters
 * rather than held alongside them, so re-opening the popover always shows the state the grid is in —
 * including after a Filters wissen or a period change.
 */
const toFormValues = ({
  organizationalUnitIds,
  entityFilters,
  employeeViewFilters
}: {
  organizationalUnitIds: BacklogOrganizationalUnitId[];
  entityFilters: BacklogEntityFilter[];
  employeeViewFilters: BacklogEmployeeViewFilters;
}): BacklogFiltersForm => {
  const perType = (entityType: BacklogEntityFilterType) =>
    entityFilters.filter((f) => f.planningEntityType === entityType);

  return {
    organizationalUnitIds,
    employeeFunctions: employeeViewFilters.functions,
    WORKPLACE: perType('WORKPLACE'),
    EMPLOYEE: perType('EMPLOYEE'),
    CLIENT: perType('CLIENT'),
    RESOURCE: perType('RESOURCE')
  };
};

const EMPTY_EMPLOYEE_VIEW_FILTERS: BacklogEmployeeViewFilters = {
  functions: []
};

const EMPTY_FORM_VALUES = toFormValues({
  organizationalUnitIds: [],
  entityFilters: [],
  employeeViewFilters: EMPTY_EMPLOYEE_VIEW_FILTERS
});

/**
 * Everything that narrows *which* tasks are listed, behind the toolbar's sliders icon: the plan unit at
 * the top, shared by every view, and below it the fields of the entity whose list is on screen.
 *
 * The chrome is the shared {@link FilterPopper} the flex match and roster plan pages use, so the
 * takenlijst gets the same look and the same behaviour for free — pin the popover open, drag it where you
 * want it, and Filters wissen / Toepassen in the footer.
 *
 * Because of that footer the filters are a *draft* until applied: picking something changes the fields
 * only, and Toepassen is what commits it to the query. That is deliberate — each applied change is a
 * server round trip, so a planner setting four filters should pay for one fetch rather than four. The
 * view settings beside this (the eye icon) still apply straight away; they only re-slice what is loaded.
 */
const BacklogFilters = ({
  entityFilters,
  onEntityFiltersChange,
  organizationalUnitIds,
  onOrganizationalUnitIdsChange,
  employeeViewFilters,
  onEmployeeViewFiltersChange,
  rowEntityType,
  startDate,
  endDate
}: BacklogFiltersProps) => {
  'use memo';

  // Popper computes placement at runtime, so it cannot come from CSS — but the layout is the
  // toolbar's, so it is read from the toolbar rather than handed over as a prop.
  const isVerticalToolbar = useIsVerticalToolbar();

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });
  const [isOpen, setIsOpen] = useState(false);

  const filterMethods = useForm<BacklogFiltersForm>({
    defaultValues: toFormValues({
      organizationalUnitIds,
      entityFilters,
      employeeViewFilters
    })
  });

  // What is applied can change without the popover: Filters wissen resets from outside the form, and a
  // remembered plan unit can drop out when it leaves the stamgegevens. Re-seeding the draft while the
  // popover is shut keeps the two from drifting; while it is open the planner's edits are left alone.
  useEffect(() => {
    if (isOpen) return;
    filterMethods.reset(
      toFormValues({
        organizationalUnitIds,
        entityFilters,
        employeeViewFilters
      })
    );
  }, [
    isOpen,
    organizationalUnitIds,
    entityFilters,
    employeeViewFilters,
    filterMethods
  ]);

  const handleClose = () => setIsOpen(false);

  const handleApplyFilters = filterMethods.handleSubmit((values) => {
    onOrganizationalUnitIdsChange(values.organizationalUnitIds);
    // Applied state rather than draft: a pick that is gone the next time the popover opens reads as lost.
    onEmployeeViewFiltersChange({ functions: values.employeeFunctions });
    // Every entity type, EMPLOYEE included. It used to be excluded because the employee field read the
    // plan unit's cost-centre list, which answers with numeric ids where the request wants planning
    // entity uuids. Since #2411 that field is the same planning-entity search the patient field is, so
    // an employee pick carries an entity uuid and travels with the rest.
    onEntityFiltersChange(
      BACKLOG_ENTITY_FILTER_TYPES.flatMap((entityType) => values[entityType])
    );
    handleClose();
  });

  const handleClearFilters = () => {
    filterMethods.reset(EMPTY_FORM_VALUES);
    onOrganizationalUnitIdsChange([]);
    onEntityFiltersChange([]);
    onEmployeeViewFiltersChange(EMPTY_EMPLOYEE_VIEW_FILTERS);
    handleClose();
  };

  // How many of the popover's fields are set — one per field with something in it, not one per value.
  // Counted over what is applied rather than the draft, so the badge reports the grid and not the popover,
  // and only over the fields the active view offers: a badge that counts a field the planner cannot see
  // sends them looking for a filter that is not there.
  const activeFilterCount = countActiveFilters({
    rowEntityType,
    organizationalUnitIds,
    entityFilters,
    employeeViewFilters
  });

  // aria-label replaces the accessible name, so the count has to be folded into it or a screen reader
  // announces the button without it — the same wording the view settings icon beside this uses.
  const accessibleLabel =
    activeFilterCount > 0
      ? `${t('filters.title')}: ${activeFilterCount}`
      : t('filters.title');

  return (
    <>
      <Tooltip
        title={t('filters.title')}
        placement={isVerticalToolbar ? 'left' : 'bottom'}
      >
        <StyledRelativeBox>
          <IconButton
            id={FILTER_ID}
            type="sliders"
            size="small"
            onClick={() => setIsOpen((open) => !open)}
            aria-label={accessibleLabel}
          />
          {activeFilterCount > 0 && (
            <PulsingBadge color="success" content={activeFilterCount} />
          )}
        </StyledRelativeBox>
      </Tooltip>
      <FormProvider {...filterMethods}>
        <FilterPopper
          isOpen={isOpen}
          filterId={FILTER_ID}
          getAnchorEl={() => document.getElementById(FILTER_ID)}
          filterMainLabel={t('filters.title')}
          // Across the top the popover drops down under the icon; docked to the side it opens into the
          // page to its left, the way the view settings beside it does.
          placement={isVerticalToolbar ? 'left' : 'bottom-start'}
          // Every filter is on show in the three rows below; there is no second tier to unfold.
          hasMoreFilters={false}
          handleClose={handleClose}
          handleClearFilters={handleClearFilters}
          handleApplyFilters={handleApplyFilters}
        >
          <BacklogFilterFields
            rowEntityType={rowEntityType}
            startDate={startDate}
            endDate={endDate}
          />
        </FilterPopper>
      </FormProvider>
    </>
  );
};

export default BacklogFilters;
